import { ActivityIndicator, Pressable, ScrollView, View } from "@-/com.npmjs.react-native";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { cn } from "@-/com.npmjs.cn";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { IconButtonHeightContext } from "@-/dev.improve.haniverse.components.ui.button.icon";
import { ProfileAvatar } from "@-/dev.improve.haniverse.components.ui.profile-card";
import { useAuthSession } from "@-/dev.improve.haniverse.lib.auth.client";
import {
  ToolbarGlassSurface,
  useToolbarGridMetrics,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { ToolbarDebugGesture } from "#toolbar-debug-gesture";
import { ToolbarExpandingPopover } from "#toolbar-expanding-popover";
import { ToolbarMenuCloseButton, ToolbarMenuPageHeader } from "#toolbar-menu-header";
import {
  ToolbarMenuContext,
  type ToolbarMenuLayout,
  type ToolbarMenuSection,
} from "#toolbar-menu-context";
import { useToolbarMenuBandHeight } from "#use-toolbar-menu-band-height";

/** Every More menu is a single titled page; the pill-tab layout is kept for opting back in. */
const DEFAULT_TOOLBAR_MENU_LAYOUT: ToolbarMenuLayout = "page";

export interface ToolbarMoreActionsProps {
  children: ReactNode;
  className?: string;
  /** Align the trigger to the trailing edge of a landscape rail. */
  expandLeft?: boolean;
  /** Show progress without disabling the menu; held briefly to avoid a flashing spinner. */
  isLoading?: boolean;
  /**
   * `"page"` (default) lists every section's content on one page under `title`; `"tabs"` shows a
   * band of section pills, each revealing only its own content.
   */
  layout?: ToolbarMenuLayout;
  /** The page layout's heading, beside the close button. Unused by the tabs layout. */
  title?: string;
}

/**
 * The header's More menu: a floating menu that covers the toolbar while its underlying pills stay
 * in place. Only a layout's main pages have one, teleported into the app shell's top-left slot
 * (`PrimaryControlsStripLeading`); a nested page with actions worth keeping embeds a
 * `ToolbarEmbeddedMoreActions` in its content instead.
 */
export function ToolbarMoreActions(props: ToolbarMoreActionsProps) {
  return <ToolbarMoreActionsMenu {...props} embedded={false} />;
}

export type ToolbarEmbeddedMoreActionsProps = Omit<ToolbarMoreActionsProps, "expandLeft">;

/**
 * A nested page's More menu, placed in the page's own content (e.g. beside the morpheme panel
 * tabs) because the header's page-menu slot holds the back button there. It opens the same tray
 * as the header's menu.
 */
export function ToolbarEmbeddedMoreActions(props: ToolbarEmbeddedMoreActionsProps) {
  return <ToolbarMoreActionsMenu {...props} embedded />;
}

function ToolbarMoreActionsMenu({
  children,
  className,
  embedded,
  expandLeft = false,
  isLoading: isLoadingProp = false,
  layout = DEFAULT_TOOLBAR_MENU_LAYOUT,
  title = "Settings",
}: ToolbarMoreActionsProps & { embedded: boolean }) {
  const isLoading = useHeldLoadingFlag(isLoadingProp);
  const { diameter } = useToolbarGridMetrics();
  const triggerContent = isLoading ? (
    <ActivityIndicator size="small" testID="toolbar-more-actions-spinner" />
  ) : (
    <Icon className="text-foreground" name="FiMoreHorizontal" size={24} />
  );
  return (
    <View
      className={cn(
        embedded
          ? "shrink-0"
          : cn("min-w-0 shrink grow", expandLeft ? "mr-0.5 items-end" : "ml-0.5 items-start"),
        className,
      )}
      pointerEvents="box-none"
    >
      <ToolbarExpandingPopover
        collapsedContent={triggerContent}
        dismissLabel="Dismiss more actions"
        headerOutset={4}
        testID="toolbar-more-actions-content"
        trigger={({ isOpen, open }) => (
          <ToolbarDebugGesture collapsedContent={triggerContent}>
            {(debugProps) => (
              <ToolbarGlassSurface style={{ height: diameter, width: diameter }}>
                <Pressable
                  {...debugProps}
                  accessibilityLabel="Show more actions"
                  accessibilityRole="button"
                  accessibilityState={{ busy: isLoading, expanded: isOpen }}
                  className="size-full items-center justify-center"
                  hitSlop={8}
                  onPress={open}
                  testID="toolbar-more-actions-trigger"
                >
                  {triggerContent}
                </Pressable>
              </ToolbarGlassSurface>
            )}
          </ToolbarDebugGesture>
        )}
      >
        {(controls) =>
          layout === "page" ? (
            <ToolbarMenuPageBody
              {...controls}
              closeLabel="Hide more actions"
              isLoading={isLoading}
              sectionTestID="toolbar-more-actions-section"
              title={title}
            >
              {children}
            </ToolbarMenuPageBody>
          ) : (
            <ToolbarMenuTabsBody
              {...controls}
              closeLabel="Hide more actions"
              isLoading={isLoading}
              sectionTestID="toolbar-more-actions-section"
            >
              {children}
            </ToolbarMenuTabsBody>
          )
        }
      </ToolbarExpandingPopover>
    </View>
  );
}

interface ToolbarMenuBodyProps {
  children: ReactNode;
  close: () => void;
  closeLabel: string;
  isLoading: boolean;
  isOpen: boolean;
  onContentSizeChange: (width: number, height: number) => void;
  sectionTestID: string;
}

/** The sections the menu's pills register, keyed by pill id in registration (tree) order. */
function useToolbarMenuSections() {
  const [sections, setSections] = useState(new Map<string, ToolbarMenuSection>());
  const register = useCallback((id: string, section: ToolbarMenuSection) => {
    setSections((previous) => new Map(previous).set(id, section));
  }, []);
  const unregister = useCallback((id: string) => {
    setSections((previous) => {
      const next = new Map(previous);
      next.delete(id);
      return next;
    });
  }, []);
  return { register, sections, unregister };
}

/**
 * The single-page menu body: the close button and `title` over every enabled section's content,
 * stacked in pill order. The pills themselves draw nothing here — they stay mounted beside the
 * title only to register their sections.
 */
function ToolbarMenuPageBody({
  children,
  close,
  closeLabel,
  isLoading,
  isOpen,
  onContentSizeChange,
  sectionTestID,
  title,
}: ToolbarMenuBodyProps & { title: string }) {
  const bandHeight = useToolbarMenuBandHeight();
  const { register, sections, unregister } = useToolbarMenuSections();
  const enabledSections = [...sections].filter(([, section]) => !section.disabled);
  const context = useMemo(
    () => ({
      activeId: undefined,
      close,
      isOpen,
      layout: "page" as const,
      register,
      select: () => {},
      unregister,
      viewId: undefined,
    }),
    [close, isOpen, register, unregister],
  );

  return (
    <ToolbarMenuContext value={context}>
      <ToolbarMenuPageHeader
        close={close}
        closeLabel={closeLabel}
        isLoading={isLoading}
        onLayout={() => {
          if (enabledSections.length === 0) {
            onContentSizeChange(0, bandHeight);
          }
        }}
        title={title}
        titleTestID="toolbar-more-actions-title"
      >
        {children}
      </ToolbarMenuPageHeader>
      <ScrollView
        className="flex-1"
        contentContainerClassName={enabledSections.length === 0 ? undefined : "gap-3 p-3"}
        keyboardShouldPersistTaps="handled"
        onContentSizeChange={(width, height) => onContentSizeChange(width, height + bandHeight)}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        testID={sectionTestID}
      >
        {enabledSections.map(([id, section], index) => (
          <Fragment key={id}>
            {index === 0 ? null : <View className="h-px bg-border" />}
            {section.content}
          </Fragment>
        ))}
      </ScrollView>
    </ToolbarMenuContext>
  );
}

/**
 * The pill-tab menu body: a horizontal band of option pills over the section each one reveals.
 */
function ToolbarMenuTabsBody({
  children,
  close,
  closeLabel,
  isLoading,
  isOpen,
  onContentSizeChange,
  sectionTestID,
}: ToolbarMenuBodyProps) {
  const { expandedControlDiameter } = useToolbarGridMetrics();
  const bandHeight = useToolbarMenuBandHeight();
  const { register, sections, unregister } = useToolbarMenuSections();
  const [selectedId, select] = useState<string>();
  const activeId =
    selectedId !== undefined && sections.has(selectedId) && !sections.get(selectedId)?.disabled
      ? selectedId
      : [...sections].find(([, section]) => !section.disabled)?.[0];
  const viewId = [...sections].find(([, section]) => section.isView && !section.disabled)?.[0];
  const context = useMemo(
    () => ({
      activeId,
      close,
      isOpen,
      layout: "tabs" as const,
      register,
      select,
      unregister,
      viewId,
    }),
    [activeId, close, isOpen, register, unregister, viewId],
  );

  return (
    <ToolbarMenuContext value={context}>
      <View
        className="shrink-0 flex-row items-center gap-1 px-1.75 pt-1.75 pb-0.75"
        onLayout={() => {
          if (activeId === undefined) {
            onContentSizeChange(0, bandHeight);
          }
        }}
        style={{ height: bandHeight }}
      >
        <ToolbarMenuCloseButton close={close} closeLabel={closeLabel} isLoading={isLoading} />
        <ScrollView
          className="min-w-0 flex-1"
          contentContainerClassName="flex-row items-center gap-1"
          horizontal
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}
          testID="toolbar-more-actions-scroll"
        >
          <IconButtonHeightContext value={expandedControlDiameter}>
            {children}
          </IconButtonHeightContext>
        </ScrollView>
      </View>
      <ScrollView
        className="flex-1"
        contentContainerClassName={activeId === undefined ? undefined : "gap-3 p-3"}
        key={activeId}
        keyboardShouldPersistTaps="handled"
        onContentSizeChange={(width, height) => onContentSizeChange(width, height + bandHeight)}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        testID={sectionTestID}
      >
        {activeId === undefined
          ? null
          : sections.get(activeId)?.isView
            ? [...sections]
                .filter(([, section]) => section.isView && !section.disabled)
                .map(([id, section]) => <Fragment key={id}>{section.content}</Fragment>)
            : sections.get(activeId)?.content}
      </ScrollView>
    </ToolbarMenuContext>
  );
}

export interface ToolbarProfileButtonProps {
  className?: string;
  onPress: () => void;
  /** The profile destination is showing: the avatar fills the whole control. */
  selected?: boolean;
}

/**
 * Settings' account control, in the slot other toolbars give their More menu. It navigates rather
 * than opening a menu, so it reads as a tab: a glass circle framing the avatar while elsewhere,
 * and the avatar itself filling the circle once its page is showing.
 */
export function ToolbarProfileButton({
  className,
  onPress,
  selected = false,
}: ToolbarProfileButtonProps) {
  const { user } = useAuthSession();
  const { diameter } = useToolbarGridMetrics();
  const avatarDiameter = Math.max(1, diameter - 16);
  const avatarSize = selected ? diameter : avatarDiameter;
  return (
    <View className={cn("ml-0.5 items-start", className)}>
      <ToolbarDebugGesture
        collapsedContent={
          <View
            className="overflow-hidden rounded-full"
            style={{ height: avatarSize, width: avatarSize }}
          >
            <ProfileAvatar user={user} />
          </View>
        }
      >
        {(debugProps) =>
          selected ? (
            <Pressable
              {...debugProps}
              accessibilityLabel="Open profile"
              accessibilityRole="button"
              accessibilityState={{ selected }}
              className="items-center justify-center overflow-hidden rounded-full border border-border/60 bg-background active:opacity-80"
              hitSlop={8}
              onPress={onPress}
              style={{ height: diameter, width: diameter }}
              testID="toolbar-profile-button"
            >
              <ProfileAvatar user={user} />
            </Pressable>
          ) : (
            <ToolbarGlassSurface style={{ height: diameter, width: diameter }}>
              <Pressable
                {...debugProps}
                accessibilityLabel="Open profile"
                accessibilityRole="button"
                accessibilityState={{ selected }}
                className="size-full items-center justify-center"
                hitSlop={8}
                onPress={onPress}
                testID="toolbar-profile-button"
              >
                <View
                  className="overflow-hidden rounded-full"
                  style={{ height: avatarDiameter, width: avatarDiameter }}
                >
                  <ProfileAvatar user={user} />
                </View>
              </Pressable>
            </ToolbarGlassSurface>
          )
        }
      </ToolbarDebugGesture>
    </View>
  );
}

/**
 * Once shown, the spinner stays up at least this long. Long enough to register as a deliberate
 * "recomputing" beat, short enough that it never feels like it is padding a finished view.
 */
const MIN_SPINNER_VISIBLE_MS = 400;

/**
 * Keeps `isLoading` true for at least {@link MIN_SPINNER_VISIBLE_MS} once it goes true.
 *
 * A view change whose data is already cached settles in well under a frame, so the raw flag can go
 * true and false between two renders. Driving the glyph straight off it makes the trigger strobe —
 * a flash of spinner that reads as a rendering glitch rather than as progress, which is worse than
 * showing nothing. Holding it briefly turns the fast case into a deliberate blink and leaves the
 * slow case (the one this feature exists for) completely unaffected.
 *
 * Rising edges are NEVER delayed — only the fall is. A genuinely slow recomputation still shows its
 * spinner on the very first render after the press.
 */
function useHeldLoadingFlag(isLoading: boolean): boolean {
  const [isHeld, setIsHeld] = useState(isLoading);
  // Written during render rather than in the effect: the effect below needs the timestamp of the
  // rising edge that is being committed NOW, and an effect ordered after it would read the previous
  // one and cut the hold short.
  //
  // Seeded from the FIRST render's value, not from 0. Mounting already-loading is a rising edge
  // too, but `useState(isLoading)` above has already made `isHeld` true, so the branch below never
  // runs for it — a 0 here would then read as "shown at the epoch", making the hold instantly
  // expired and defeating the whole mechanism on the mount path.
  const shownAtRef = useRef(isLoading ? Date.now() : 0);
  if (isLoading && !isHeld) {
    shownAtRef.current = Date.now();
    setIsHeld(true);
  }

  useEffect(() => {
    if (isLoading || !isHeld) {
      return;
    }
    const remainingMs = MIN_SPINNER_VISIBLE_MS - (Date.now() - shownAtRef.current);
    if (remainingMs <= 0) {
      setIsHeld(false);
      return;
    }
    const timer = setTimeout(() => {
      setIsHeld(false);
    }, remainingMs);
    return () => {
      clearTimeout(timer);
    };
  }, [isHeld, isLoading]);

  return isHeld;
}
